import React, { useEffect, useRef } from "react";
import clsx from 'clsx'
import { FORM_ELEMENT_HEIGHT } from "@/constants/styles";

import AppBox from "@/components/AppBox";
import AppText from "@/components/AppText";

interface AppInputProps extends React.InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement> {
  label?: React.ReactNode;
  value: string;
  fullWidth?: boolean;
  onPressEnter?: () => void;
  onChange?: (value: any) => void;
  error?: string | null; // Error message
  startAdornment?: React.ReactNode; // Start adornment
  endAdornment?: React.ReactNode; // End adornment
  multiline?: boolean; // Support for multiline input
  rows?: number; // Number of rows for multiline input
  className?: string;
  inputClassName?: string;
  disabled?: boolean;
  inputSize?: 'default' | 'small';
  autoResize?: boolean; // New prop for auto-resizing
  resize?: 'none' | 'both' | 'horizontal' | 'vertical'; // New prop for manual resize
  noFocusRing?: boolean; // disable default focus ring classes
}

const AppInput: React.FC<AppInputProps> = ({
  label,
  value,
  fullWidth = false,
  onChange,
  onPressEnter,
  error = null,
  startAdornment,
  endAdornment,
  multiline = false,
  rows = 3,
  className = "",
  inputClassName = "",
  disabled = false,
  inputSize = 'default',
  autoResize = false,
  resize = 'none',
  noFocusRing = false,
  ...props
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize functionality
  useEffect(() => {
    if (multiline && autoResize && textareaRef.current) {
      const textarea = textareaRef.current;
      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = 'auto';
      // Set the height to scrollHeight to fit content
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, [value, multiline, autoResize]);

  // Handle input or textarea change
  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (onChange) {
      onChange(event.target.value);
    }
  };

  // Handle "Enter" key press
  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (event.key === "Enter" && !multiline && onPressEnter) {
      onPressEnter();
    }
  };

  const classes = clsx(
    "transition-all duration-300 ease-in-out border rounded-md",
    !noFocusRing && "focus-within:ring-2 focus-within:ring-primary",
    error ? "border-error ring-red-200" : "border-gray-300",
    inputSize === 'small' ? "px-2 py-1" : "px-2 py-2",
    className,
    !multiline ? FORM_ELEMENT_HEIGHT : ""
  );

  const inputClasses = clsx(
    "flex-1 outline-none bg-transparent border-transparent w-full",
    {
      "text-gray-400": disabled,
      "text-gray-900": !disabled,
    },
    inputClassName
  );

  return (
    <AppBox column fullWidth={fullWidth} gap={1}>
      {/* Label */}
      {label && (
        <>
          {typeof label === "string" ? (
            <AppText 
              color={disabled ? "disabled" : "dark"} 
              variant="label"
              className={inputSize === 'small' ? "text-sm" : ""}
            >
              {label}
            </AppText>
          ) : (
            label // Render ReactNode directly
          )}
        </>
      )}

      {/* Input Container */}
      <AppBox
        align="center"
        gap={2}
        className={classes}
      >
        {/* Start Adornment */}
        {startAdornment && <AppBox>{startAdornment}</AppBox>}

        {/* Input Field */}
        {multiline ? (
          <textarea
            ref={textareaRef}
            className={inputClasses}
            style={{ 
              resize,
              minHeight: `${rows * 1.5}em`,
              overflow: 'hidden'
            }}
            value={value}
            rows={rows}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            {...props}
          />
        ) : (
          <input
            className={inputClasses}
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            {...props}
          />
        )}

        {/* End Adornment */}
        {endAdornment && <AppBox>{endAdornment}</AppBox>}
      </AppBox>

      {/* Error Text */}
      {error && (
        <AppText 
          variant="caption" 
          color="error"
          className={inputSize === 'small' ? "text-xs" : ""}
        >
          {error}
        </AppText>
      )}
    </AppBox>
  );
};

export default AppInput;